import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  careManagerApi,
  InputMaybe,
  PatientFragment,
  PatientGenderIdentity,
  PatientLegalSex,
  PatientPronounsEnum,
  patientRegistrationApi,
  QuestionnaireName,
  UpdatePatientCareInput,
  UpdatePatientInput,
} from '@valerahealth/rtk-query'
import { useSelector } from 'react-redux'
import { useTranslation } from '@valerahealth/ui-translation'
import {
  Typography,
  Divider,
  Grid,
  Box,
  useNotify,
  Stack,
  setIntlPhoneNumber,
  parseIntlPhoneNumber,
} from '@valerahealth/ui-components'
import {
  FloatingSaveButton,
  FormProvider,
  onlyDirtyFields,
  useForm,
} from '@valerahealth/ui-components/form'
import { RootState } from 'redux/reducers'
import {
  dateToStr,
  getUSTimezone,
  strToDate,
  SYSTEM_TIMEZONE,
} from '@valerahealth/ui-components/utils/date'
import { useOutletContext } from 'react-router'
import { useReduxDispatch, useReduxSelector } from 'redux/store'
import { isWellnessSelector } from 'redux/selectors'
import { TreatmentRoomRouteContext } from '../utilities/treatments'
import PatientDetails from './PatientDetails'
import OnBoarding from './OnBoarding'
import AppUsage from './AppUsage'
import PeripheralDevices from './PeripheralDevices'
import PhoneAlreadyExist from '../common/PhoneAlreadyExist'
import { SIDEBAR_WIDTH } from '../TreatmentRoom'

type DisplayAlreadyType = {
  sameClinic: boolean
  data: UpdatePatientInput
}

const patientToForm = (patient?: PatientFragment) => {
  const { care, demographics } = patient || {}

  const {
    scaleId,
    hospitalized,
    disableChat,
    diagnosis,
    device,
    medicalDiagnosis,
    questionnaires,
    referralSource,
  } = care || {}

  const {
    patientId,
    firstName,
    middleName,
    lastName,
    username,
    preferredName,
    pronouns,
    mrn,
    gender,
    genderIdentity,
    genderDetails,
    phone,
    primaryPhone,
    email,
    primaryEmail,
    status,
    dateOfBirth,
    timezone,
    picture,
    languages,
    preferredLanguage,
    videoLink,
    addressComponents,
  } = demographics || {}

  return {
    picture: picture || '',
    profileId: patientId || '',
    firstName: firstName || '',
    lastName: lastName || '',
    addressComponents: addressComponents || {
      street: '',
      city: '',
      state: '',
      zipcode: '',
    },
    gender: gender || PatientLegalSex.O,
    genderIdentity: genderIdentity || PatientGenderIdentity.Other,
    genderDetails: genderDetails || '',
    phone: phone ? parseIntlPhoneNumber(phone.trim()) : '',
    primaryPhone: primaryPhone ? parseIntlPhoneNumber(primaryPhone.trim()) : '',
    email: email || '',
    primaryEmail: primaryEmail || '',
    dateOfBirth: dateOfBirth ? strToDate(dateOfBirth) : null,
    status: status!,
    middleName: middleName || '',
    preferredLanguage: preferredLanguage || '',
    languages: languages || [],
    username: username || '',
    mrn: mrn || '',
    diagnosis: diagnosis || [],
    medicalDiagnosis: medicalDiagnosis || [],
    scaleId: scaleId || '',
    phqScore:
      questionnaires?.find((x) => x && x.name === QuestionnaireName.Phq)
        ?.score || '',
    gad7Score:
      questionnaires?.find((x) => x && x.name === QuestionnaireName.Gad7)
        ?.score || '',
    videoLink: videoLink || '',
    useMobileApp: (device && device.useMobileApp) || false,
    patientMessaging: !disableChat,
    preferredName: preferredName || '',
    pronouns: pronouns || PatientPronounsEnum.Other,
    timezone: timezone || getUSTimezone(SYSTEM_TIMEZONE),
    hospitalized: hospitalized || false,
    referralSource: referralSource || '',
  }
}

export type PatientBasicInfoForm = ReturnType<typeof patientToForm>

const formToPatient = (
  submittedData: Partial<PatientBasicInfoForm>,
): UpdatePatientInput => {
  const {
    picture,
    profileId,
    firstName,
    lastName,
    addressComponents,
    gender,
    genderIdentity,
    genderDetails,
    phone,
    email,
    primaryPhone,
    primaryEmail,
    dateOfBirth,
    status,
    middleName,
    preferredLanguage,
    languages,
    username,
    mrn,
    diagnosis,
    preferredName,
    pronouns,
    timezone,
    hospitalized,
    useMobileApp,
    patientMessaging,
    scaleId,
    referralSource,
  } = submittedData || {}

  const response: UpdatePatientInput = {
    patientId: profileId!,
  }

  const demographics = {
    firstName,
    middleName: middleName === '' ? null : middleName,
    lastName,
    username,
    preferredName: preferredName === '' ? null : preferredName,
    pronouns,
    mrn,
    gender,
    genderIdentity,
    genderDetails: genderDetails === '' ? null : genderDetails,
    addressComponents: addressComponents
      ? {
          street:
            addressComponents.street === '' ? null : addressComponents.street,
          city: addressComponents.city === '' ? null : addressComponents.city,
          state: addressComponents.state,
          zipcode:
            addressComponents.zipcode === '' ? null : addressComponents.zipcode,
        }
      : undefined,
    phone: phone ? setIntlPhoneNumber(phone.trim()) : undefined,
    primaryPhone: primaryPhone
      ? setIntlPhoneNumber(primaryPhone.trim())
      : undefined,
    email,
    primaryEmail,
    status,
    dateOfBirth: dateOfBirth ? dateToStr(dateOfBirth) : undefined,
    timezone,
    picture,
    languages,
    preferredLanguage,
    tags: diagnosis as string[],
  }

  const care: InputMaybe<UpdatePatientCareInput> = {
    hospitalized,
    referralSource: referralSource || undefined,
    scaleId: scaleId || undefined,
    ...(typeof patientMessaging === 'boolean'
      ? {
          disableChat: !patientMessaging,
        }
      : {}),
    ...(typeof useMobileApp === 'boolean'
      ? {
          device: {
            type: undefined,
            useMobileApp,
          },
        }
      : {}),
  }

  if (Object.values(demographics).some((x) => x !== undefined))
    response.demographics = demographics
  if (Object.values(care).some((x) => x !== undefined)) response.care = care
  return response
}

const TreatmentBasicInfo = () => {
  const notify = useNotify()
  const { t } = useTranslation()
  const [isSaveSuccess, setIsSaveSuccess] = useState(false)
  const isWellness = useReduxSelector(isWellnessSelector)
  const [updatePatient, { isSuccess, isError }] =
    patientRegistrationApi.useUpdatePatientMutation()
  const dispatch = useReduxDispatch()
  const { openSidebar } = useSelector((state: RootState) => state.global)
  const { treatmentId, patient } = useOutletContext<TreatmentRoomRouteContext>()

  useEffect(() => {
    setIsSaveSuccess(isSuccess)
  }, [isSuccess])

  const [displayAlreadyExist, setDisplayAlreadyExist] =
    useState<DisplayAlreadyType | null>(null)

  const methods = useForm({
    defaultValues: patientToForm(patient),
  })

  const { reset } = methods

  useEffect(() => {
    if (patient) {
      reset(patientToForm(patient))
    }
  }, [patient, reset])

  const integrationLink = 'https://www.fitbit.com/global/us/connect123'
  const peripheralDevice = useCallback(
    () => <PeripheralDevices link={integrationLink} />,
    [integrationLink],
  )

  const sections = useMemo(
    () =>
      isWellness
        ? [
            {
              title: t('patientDetails'),
              component: PatientDetails,
            },
            {
              title: t('onboarding'),
              component: OnBoarding,
            },
          ]
        : [
            {
              title: t('patientDetails'),
              component: PatientDetails,
            },
            {
              title: t('onboarding'),
              component: OnBoarding,
            },
            {
              title: t('valeraAppUsage'),
              component: AppUsage,
            },
            {
              title: t('peripheralDevices'),
              component: peripheralDevice,
            },
          ],
    [isWellness, peripheralDevice, t],
  )

  const updateData = async (
    input: UpdatePatientInput,
    forceMultiPhone?: boolean,
  ) => {
    const configuration =
      typeof forceMultiPhone === 'boolean'
        ? {
            ...input.configuration,
            forceMultiPhone: forceMultiPhone || false,
          }
        : undefined

    const res = await updatePatient({
      input: {
        ...input,
        configuration,
      },
    })

    if ('error' in res) {
      const { message } = res.error
      if (message === 'already_exists_in_same_clinic')
        setDisplayAlreadyExist({ sameClinic: true, data: input })
      else if (message === 'already_exists_in_different_clinic')
        setDisplayAlreadyExist({ sameClinic: true, data: input })
      else
        notify({
          message: message || t('serverError.500'),
          severity: 'error',
        })
      return
    }

    dispatch(
      careManagerApi.util.invalidateTags([
        {
          id: treatmentId,
          type: 'PatientProfile',
        },
      ]),
    )

    dispatch(
      patientRegistrationApi.util.invalidateTags([
        {
          id: treatmentId,
          type: 'patient',
        },
      ]),
    )

    reset(undefined, {
      keepValues: true,
      keepDirty: false,
    })

    notify({
      message: t('patientSuccessfullyUpdated'),
      severity: 'success',
    })

    setIsSaveSuccess(false)
  }

  const onFormSubmit = async (patientForm: PatientBasicInfoForm) => {
    const { profileId, dateOfBirth, ...rest } = patientForm
    const { dateOfBirth: _dateOfBirth } = methods.formState.dirtyFields

    const _input = formToPatient({
      ...onlyDirtyFields(methods.formState.dirtyFields, {
        ...rest,
      }),
      dateOfBirth: _dateOfBirth ? dateOfBirth : undefined,
      profileId,
    })

    await updateData(_input)
  }

  const onFormValidationFail = () => {
    notify({
      message: t('form_invalid'),
      severity: 'warning',
    })
  }

  return (
    <>
      {displayAlreadyExist && (
        <PhoneAlreadyExist
          handleClose={() => {
            setDisplayAlreadyExist(null)
          }}
          sameClinic={displayAlreadyExist?.sameClinic}
          handleContinue={() => {
            updateData(displayAlreadyExist.data, true)
          }}
        />
      )}

      <Typography style={{ overflow: 'auto' }}>
        <Grid className="initial-css" sx={{ p: 3 }}>
          <FormProvider {...methods}>
            <Stack
              position="relative"
              spacing={3}
              component="form"
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  return false
                }
              }}
              onSubmit={methods.handleSubmit(
                onFormSubmit,
                onFormValidationFail,
              )}
            >
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Typography variant="h5" gutterBottom component="div">
                    {t('basicInfo')}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                sx={{ padding: '20px 10px', m: 0 }}
                style={{ marginTop: 0 }}
              >
                {sections.map((section) => {
                  const Section = section.component

                  return (
                    <Box key={section.title}>
                      <Typography gutterBottom variant="h6">
                        {section.title}
                      </Typography>
                      <Grid sx={{ p: 2 }} style={{ position: 'relative' }}>
                        <Section />
                      </Grid>
                      <Divider sx={{ mb: 4, mt: 4 }} />
                    </Box>
                  )
                })}
              </Grid>
              <FloatingSaveButton
                boxSx={
                  openSidebar
                    ? {
                        right: `${SIDEBAR_WIDTH + 2}%`,
                      }
                    : undefined
                }
                sx={{
                  opacity: 1,
                }}
                label={t('save')}
                isError={isError}
                isSuccess={isSaveSuccess}
              />
            </Stack>
          </FormProvider>
        </Grid>
      </Typography>
    </>
  )
}

export default TreatmentBasicInfo
